import { useCallback, useEffect, useState } from "react";
import {
  Plus, Users, ClipboardList, Copy, Check, Download, ArrowLeft, Trash2, Flame, QrCode, Pencil, X, Loader2,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  listClassrooms, getClassroom, gradebookCsvUrl, removeStudentFromClass, updateClassroom,
} from "@/lib/api";
import { SeatDashboard } from "@/components/SeatDashboard";
import { AssignmentDetail } from "./AssignmentDetail";
import { CreateAssignmentModal } from "./CreateAssignmentModal";
import { ClassLibrary } from "./ClassLibrary";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Classroom detail — the meat of the instructor UX: join code hero, roster,
// class library (private staging area), and assignment feed.
export const ClassroomDetail = ({ classroom: initial, onBack, onPreviewLecture, onGoCreate }) => {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";
  const [classroom, setClassroom] = useState(initial);
  const [assignmentModal, setAssignmentModal] = useState(false);
  const [assignPreselect, setAssignPreselect] = useState({ id: "", title: "" });
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [copied, setCopied] = useState(false);
  const [libRefresh, setLibRefresh] = useState(0);
  const [allMyClasses, setAllMyClasses] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [editCode, setEditCode] = useState(false);

  // Memoize `refresh` on classroom.id so the effect can depend on it AND we
  // auto-refetch if the parent ever swaps in a different classroom prop.
  const refresh = useCallback(() => {
    return getClassroom(classroom.id).then(setClassroom).catch(() => {});
  }, [classroom.id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    listClassrooms().then(setAllMyClasses).catch(() => setAllMyClasses([]));
  }, []);
  const otherClasses = allMyClasses.filter((c) => c.id !== classroom.id);

  const copyCode = async () => {
    // Deep-link into the student join page with the code pre-filled.
    const joinLink = `${window.location.origin}/join/${classroom.code}`;
    await navigator.clipboard.writeText(joinLink);
    setCopied(true);
    toast.success("Join link copied — send it to your class");
    setTimeout(() => setCopied(false), 2000);
  };

  const joinLink = typeof window !== "undefined"
    ? `${window.location.origin}/join/${classroom.code}`
    : `/join/${classroom.code}`;

  const removeStudent = async (sid) => {
    if (!window.confirm("Remove this student from the class?")) return;
    await removeStudentFromClass(classroom.id, sid);
    toast.success("Removed");
    refresh();
  };

  const openAssignFromLibrary = (lecture) => {
    setAssignPreselect({ id: lecture.id, title: `${lecture.chapter_label || lecture.chapters || "Lecture"} · ${lecture.book_title || ""}`.trim() });
    setAssignmentModal(true);
  };
  const openBlankAssignment = () => {
    setAssignPreselect({ id: "", title: "" });
    setAssignmentModal(true);
  };
  const onAssignmentCreated = () => {
    refresh();
    setLibRefresh((n) => n + 1);
  };

  if (selectedAssignment) {
    return <AssignmentDetail classroom={classroom} assignment={selectedAssignment} onClose={() => { setSelectedAssignment(null); refresh(); }} onPreviewLecture={onPreviewLecture} />;
  }

  const roster = classroom.roster || [];
  const assignments = classroom.assignments || [];

  return (
    <div>
      <button onClick={onBack} data-testid="back-to-classes" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
        <ArrowLeft className="h-4 w-4" /> All classes
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl font-bold">{classroom.name}</h2>
          {classroom.subject && <p className="mt-1 text-sm text-muted-foreground">{classroom.subject}</p>}
        </div>
        <div className="flex items-center gap-2">
          <a href={gradebookCsvUrl(classroom.id)} target="_blank" rel="noreferrer" data-testid="gradebook-csv"
             className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60 transition">
            <Download className="h-4 w-4" /> CSV
          </a>
          <button data-testid="new-assignment-btn" onClick={openBlankAssignment}
                  className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition">
            <Plus className="h-4 w-4" /> New assignment
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border-2 border-dashed border-accent/40 bg-accent/5 p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Class join code</p>
              <button
                data-testid="edit-class-code"
                onClick={() => setEditCode(true)}
                className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition"
                title="Change this code">
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>
            <p data-testid="class-code" className="mt-1 font-mono text-3xl sm:text-4xl font-bold tracking-widest break-all">{classroom.code}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Students sign up and enter this code to join.
              {" "}
              <span className="font-semibold text-foreground" data-testid="seat-count">
                {(classroom.roster || []).length}
                {classroom.seat_limit ? ` / ${classroom.seat_limit}` : " / ∞"} seats
              </span>
              {classroom.seat_limit && !isPro && (classroom.roster || []).length >= classroom.seat_limit && (
                <span className="ml-2 inline-flex items-center rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[10px] font-bold">FULL</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowQR(true)} data-testid="show-qr-btn"
                    className="inline-flex items-center gap-2 rounded-xl border-2 border-border px-4 py-3 font-semibold hover:bg-secondary/60 transition">
              <QrCode className="h-4 w-4" /> QR
            </button>
            <button onClick={copyCode} data-testid="copy-join-link"
                    className="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-3 font-semibold hover:opacity-90 transition">
              {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy join link</>}
            </button>
          </div>
        </div>
      </div>

      {/* QR dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="max-w-sm">
          <DialogTitle className="font-serif text-xl font-bold">Scan to join</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Students: open your phone camera and point at the code below.
          </DialogDescription>
          <div className="mt-4 flex flex-col items-center gap-3">
            <div className="rounded-2xl bg-white p-4 shadow">
              <QRCodeSVG value={joinLink} size={220} level="M" />
            </div>
            <p className="font-mono text-sm tracking-widest break-all text-center">{classroom.code}</p>
            <p className="text-xs text-muted-foreground text-center break-all">{joinLink}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit code dialog */}
      <EditCodeDialog
        open={editCode}
        onClose={() => setEditCode(false)}
        classroom={classroom}
        isPro={isPro}
        onSaved={(upd) => { setClassroom({ ...classroom, ...upd }); refresh(); }}
      />

      <h3 className="mt-8 mb-3 flex items-center gap-2 font-serif text-xl font-bold">
        <Users className="h-5 w-5" /> Roster · {roster.length}
      </h3>
      {roster.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
          No students yet. Share the code above.
        </p>
      ) : (
        <ul className="rounded-2xl border border-border divide-y divide-border">
          {roster.map((s) => (
            <li key={s.student_id} data-testid={`roster-${s.student_id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="grid place-items-center h-9 w-9 rounded-full bg-secondary text-sm font-bold">
                {(s.name || s.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{s.name || s.email.split("@")[0]}</div>
                <div className="text-xs text-muted-foreground truncate">{s.email}</div>
              </div>
              {s.current_streak > 0 && (
                <span className="text-xs font-semibold text-accent flex items-center gap-1"><Flame className="h-3 w-3" /> {s.current_streak}</span>
              )}
              <button onClick={() => removeStudent(s.student_id)} data-testid={`remove-${s.student_id}`}
                      className="p-2 text-muted-foreground hover:text-destructive transition" title="Remove from class">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-10">
        <SeatDashboard classroom={classroom} />
      </div>
      <div className="mt-10">
        <ClassLibrary
          classroom={classroom}
          onAssignFromLibrary={openAssignFromLibrary}
          onPreview={onPreviewLecture}
          onUpload={onGoCreate}
          otherClasses={otherClasses}
          refreshTick={libRefresh}
        />
      </div>

      <h3 className="mt-10 mb-3 flex items-center gap-2 font-serif text-xl font-bold">
        <ClipboardList className="h-5 w-5" /> Assignments · {assignments.length}
      </h3>
      {assignments.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-8 text-center">
          <p className="text-sm text-muted-foreground">No assignments yet.</p>
          <button onClick={openBlankAssignment}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2 text-sm font-semibold hover:opacity-90 transition">
            <Plus className="h-4 w-4" /> Create your first assignment
          </button>
        </div>
      ) : (
        <ul className="grid gap-3">
          {assignments.map((a) => (
            <li key={a.id} data-testid={`asg-${a.id}`}>
              <button onClick={() => setSelectedAssignment(a)}
                      className="w-full text-left rounded-2xl border border-border bg-card p-4 hover:border-accent/60 transition">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg font-bold">{a.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {a.mode === "shared" ? "Shared lecture" : `Each student → ${a.book_title}${a.chapters ? ` · ${a.chapters}` : ""}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                    {a.min_score > 0 && <span className="rounded-full bg-secondary px-2 py-0.5">≥ {Math.round(a.min_score)}%</span>}
                    <span>{a.submission_count} done</span>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <CreateAssignmentModal open={assignmentModal} onClose={() => setAssignmentModal(false)}
                             classroom={classroom} onCreated={onAssignmentCreated}
                             preselectedLectureId={assignPreselect.id}
                             preselectedTitle={assignPreselect.title} />
    </div>
  );
};

// Inline modal for renaming the join code and adjusting seat limit.
// Scoped here to avoid an extra file for a tiny dialog that only this page uses.
const EditCodeDialog = ({ open, onClose, classroom, isPro, onSaved }) => {
  const [code, setCode] = useState(classroom.code || "");
  const [seatLimit, setSeatLimit] = useState(classroom.seat_limit || 30);
  const [unlimited, setUnlimited] = useState((classroom.seat_limit || 0) === 0);
  const [tone, setTone] = useState(classroom.tone || "teen");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(classroom.code || "");
      setSeatLimit(classroom.seat_limit || 30);
      setUnlimited((classroom.seat_limit || 0) === 0);
      setTone(classroom.tone || "teen");
    }
  }, [open, classroom.code, classroom.seat_limit, classroom.tone]);

  const codeHint = (() => {
    const c = (code || "").trim().toUpperCase();
    if (!c) return "Required";
    if (c.length < 4 || c.length > 24) return "4-24 characters";
    if (!/^[A-Z0-9-]+$/.test(c)) return "Letters, numbers, dashes only";
    if (c.startsWith("-") || c.endsWith("-") || c.includes("--")) return "Dashes can't start, end, or repeat";
    return null;
  })();

  const save = async (e) => {
    e.preventDefault();
    if (codeHint) { toast.error(codeHint); return; }
    setSaving(true);
    try {
      const payload = {};
      const upper = code.trim().toUpperCase();
      if (upper !== classroom.code) payload.code = upper;
      const newLimit = isPro ? (unlimited ? 0 : Math.max(1, parseInt(seatLimit) || 30)) : 30;
      if (newLimit !== (classroom.seat_limit || 0)) payload.seat_limit = newLimit;
      if (tone !== classroom.tone) payload.tone = tone;
      if (Object.keys(payload).length === 0) {
        toast.success("Nothing to update");
        onClose();
        return;
      }
      const res = await updateClassroom(classroom.id, payload);
      toast.success("Updated");
      onSaved({
        code: res.code ?? payload.code ?? classroom.code,
        seat_limit: res.seat_limit ?? payload.seat_limit ?? classroom.seat_limit,
        tone: res.tone ?? payload.tone ?? classroom.tone,
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogTitle className="sr-only">Edit classroom settings</DialogTitle>
        <DialogDescription className="sr-only">Change this classroom's join code, seat limit, or tone.</DialogDescription>
        <form onSubmit={save}>
          <div className="p-6 pb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="font-serif text-xl font-bold">Classroom settings</h2>
              <p className="mt-1 text-sm text-muted-foreground">Rename the code, adjust seats, change tone.</p>
            </div>
            <button type="button" onClick={onClose}
                    className="text-muted-foreground hover:text-foreground p-1" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-6 pb-6 space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Join code</label>
              <input data-testid="edit-code-input" value={code}
                     onChange={(e) => setCode(e.target.value.toUpperCase())}
                     maxLength={24}
                     className={`mt-2 w-full font-mono tracking-widest rounded-xl border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60 ${codeHint ? "border-destructive" : "border-input"}`} />
              <p className={`mt-1.5 text-xs ${codeHint ? "text-destructive" : "text-muted-foreground"}`}>
                {codeHint || "Students will need the new code to join from now on."}
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> Seat limit
              </label>
              {!isPro ? (
                <div className="mt-2 rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3">
                  <p className="text-sm"><span className="font-bold">30 students</span><span className="text-muted-foreground"> · free plan</span></p>
                  <p className="mt-1 text-xs text-muted-foreground">Upgrade to Pro for unlimited seats.</p>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid="edit-seat-unlimited"
                           checked={unlimited}
                           onChange={(e) => setUnlimited(e.target.checked)}
                           className="h-4 w-4 rounded accent-accent" />
                    Unlimited
                  </label>
                  {!unlimited && (
                    <div className="flex items-center gap-3">
                      <input type="range" min="5" max="200" step="5"
                             value={seatLimit}
                             onChange={(e) => setSeatLimit(parseInt(e.target.value))}
                             className="flex-1 accent-accent" />
                      <span className="font-mono text-sm font-bold w-14 text-right">{seatLimit}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tone</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  { id: "kids", label: "Kids" },
                  { id: "teen", label: "Teen" },
                  { id: "college", label: "College" },
                ].map((t) => (
                  <button key={t.id} type="button" data-testid={`edit-tone-${t.id}`}
                          onClick={() => setTone(t.id)}
                          className={`rounded-xl border-2 px-3 py-2 text-sm font-semibold transition ${tone === t.id ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" data-testid="edit-code-save"
                    disabled={saving || !!codeHint}
                    className="w-full rounded-xl bg-accent text-accent-foreground py-3 font-semibold disabled:opacity-60 hover:opacity-90 transition">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Save changes"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
